import type { ParseKeys } from "i18next";
import {
  product_variant_cost_route,
  products_route,
  users_sku_settings_generate_route,
  VITE_API_URL,
} from "@/constants";
import type { AuthFetchParams } from "./global";
import type {
  SingleProduct,
  ProductsResponse,
  ProductMinimal,
  MinimalSingleProduct,
} from "@/lib/types";
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
} from "@tanstack/react-query";
import { useAuth } from "@/providers/auth";
import { toast } from "sonner";
import i18n from "@/i18n";
import { queryClient } from "@/main";
import { useOrg } from "@/providers/organization";
import { isTokenInvalid } from "@/lib/api-client";

interface BulkProductParams {
  ids: string[];
  all: boolean;
  // all acts on the products the list shows with these
  filters?: BulkFilters;
  // how many products it acts on, the list's total with all
  count?: number;
}

export interface InventoryFilters {
  sync: "all" | "synced" | "unsynced";
  pcIds: string[];
  // the products in any of these collections, none lists every product
  collectionIds: string[];
}

// a product list's filters, a bulk action on all of its products acts on the
// ones they list
export interface BulkFilters {
  search: string;
  sync_status: InventoryFilters["sync"];
  pc_ids: string[];
  collection_ids: string[];
}

// the filters the list was requested with, see getProducts
export const bulkFilters = (
  filters: InventoryFilters,
  searchTerm: string,
): BulkFilters => ({
  search: serverSearch(searchTerm),
  sync_status: filters.sync,
  pc_ids: filters.pcIds,
  collection_ids: filters.collectionIds,
});

export interface ProductSort {
  by: "title" | "sku" | "quantity";
  dir: "asc" | "desc";
}

export const DEFAULT_PRODUCT_SORT: ProductSort = { by: "title", dir: "asc" };

export const PRODUCT_SORT_OPTIONS: Array<{
  value: `${ProductSort["by"]}-${ProductSort["dir"]}`;
  by: ProductSort["by"];
  dir: ProductSort["dir"];
  // translation key
  label: ParseKeys;
}> = [
  { value: "title-asc", by: "title", dir: "asc", label: "table.sort.titleAsc" },
  {
    value: "title-desc",
    by: "title",
    dir: "desc",
    label: "table.sort.titleDesc",
  },
  { value: "sku-asc", by: "sku", dir: "asc", label: "table.sort.skuAsc" },
  { value: "sku-desc", by: "sku", dir: "desc", label: "table.sort.skuDesc" },
  {
    value: "quantity-asc",
    by: "quantity",
    dir: "asc",
    label: "table.sort.quantityAsc",
  },
  {
    value: "quantity-desc",
    by: "quantity",
    dir: "desc",
    label: "table.sort.quantityDesc",
  },
];

async function getProducts(
  params: AuthFetchParams,
  page: number,
  pageSize: number,
  syncFiters: InventoryFilters,
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) {
  const search = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const trimmedSearchTerm = searchTerm.trim();
  if (trimmedSearchTerm) {
    search.set("search", trimmedSearchTerm);
  }
  search.set("sync_status", syncFiters.sync);
  search.set("pc_ids", syncFiters.pcIds.join(",") ?? "");
  if (syncFiters.collectionIds.length > 0) {
    search.set("collection_ids", syncFiters.collectionIds.join(","));
  }
  search.set("sort_by", sort.by);
  search.set("sort_dir", sort.dir);
  const res = await fetch(`${VITE_API_URL}${products_route}?${search}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  if (!res.ok) {
    throw new Error("Failed to load products");
  }

  const resp = (await res.json()) as ProductsResponse;
  return resp;
}

async function getProduct(id: string, params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${products_route}/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  const product = (await res.json()) as SingleProduct;

  return {
    id: product.title,
    title: product.title,
    sku: product.sku,
    variants: product.variants.map((variant) => ({
      id: variant.id,
      title: variant.title,
      sku: variant.sku,
      availableQty: variant.available_qty,
      cost: variant.cost_price,
      price: variant.current_price,
      platforms: variant.platforms,
    })),
  } as MinimalSingleProduct;
}

export const productQueryOpts = (id: string, params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["products", id],
    queryFn: async () => await getProduct(id, params),
  });

export const MIN_SEARCH_LENGTH = 3;

// The search the server is asked for, shorter ones show every product: the
// server can't use its search index for them and would read every product of
// a large store.
export const serverSearch = (searchTerm: string) => {
  const trimmed = searchTerm.trim();
  return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : "";
};

export const productsQueryOpts = (
  params: AuthFetchParams,
  syncFilters: InventoryFilters,
  page = 1,
  pageSize = 50,
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) => {
  const search = serverSearch(searchTerm);
  return queryOptions({
    queryKey: ["products", page, pageSize, search, syncFilters, sort],
    queryFn: async () =>
      await getProducts(params, page, pageSize, syncFilters, search, sort),
    staleTime: 5 * 60 * 1000,
  });
};

// every store, the API fills them in when none are given
const ALL_PRODUCTS: InventoryFilters = {
  sync: "all",
  pcIds: [],
  collectionIds: [],
};

/**
 * Pages of products for pickers that load more as they scroll. Kept apart from
 * ["products"], which the inventory mutations rewrite as a single page.
 */
export const productPickerQueryOpts = (
  params: AuthFetchParams,
  searchTerm = "",
  pageSize = 30,
) => {
  const search = serverSearch(searchTerm);
  return infiniteQueryOptions({
    queryKey: ["product-picker", search, pageSize],
    queryFn: async ({ pageParam }) =>
      await getProducts(params, pageParam, pageSize, ALL_PRODUCTS, search),
    initialPageParam: 1,
    getNextPageParam: ({ pagination }) =>
      pagination.page < pagination.totalPages ? pagination.page + 1 : undefined,
    staleTime: 60 * 1000,
  });
};

export const useToggleProductSync = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();
  return useMutation({
    mutationFn: async ({ ids, all, filters }: BulkProductParams) => {
      const res = await fetch(`${VITE_API_URL}${products_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          ids,
          all,
          filters,
        }),
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      return await res.json();
    },
    onMutate: async ({ ids, all }: BulkProductParams) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["products"]);

      // Optimistically update all products queries
      queryClient.setQueriesData(
        { queryKey: ["products"] },
        (old: ProductsResponse | undefined) => {
          if (!old) return old;

          return {
            ...old,
            products: old.products.map((product) =>
              all || ids.includes(product.id)
                ? { ...product, is_synced: !product.is_synced }
                : product,
            ),
          };
        },
      );

      return { previousData };
    },
    onSuccess: () => {
      toast.success(i18n.t("toast.productSyncUpdated"));
    },
    onError: (err, _ids, context) => {
      console.log(err);
      toast.error(i18n.t("toast.productSyncFailed"));

      // Revert the optimistic update
      if (context?.previousData) {
        queryClient.setQueriesData(
          { queryKey: ["products"] },
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to sync with server
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useGenerateProductSku = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();
  return useMutation({
    mutationFn: async ({ ids, all, filters }: BulkProductParams) => {
      const res = await fetch(
        `${VITE_API_URL}${users_sku_settings_generate_route}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
            "X-Inventoree-Organization": orgId,
          },
          credentials: "include",
          body: JSON.stringify({
            ids,
            all,
            filters,
          }),
        },
      );

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      return await res.json();
    },
    onMutate: async ({ ids }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["products"]);

      return { previousData, ids };
    },
    onSuccess: (data) => {
      toast.success(i18n.t("toast.skusGenerated"));

      // Update the cache with the new SKU data from the server response
      queryClient.setQueriesData(
        { queryKey: ["products"] },
        (old: ProductsResponse | undefined) => {
          if (!old || !data?.products) return old;

          // Create a map of updated products for quick lookup
          const updatedProductsMap = new Map(
            data.products.map((product: ProductMinimal) => [
              product.id,
              product,
            ]),
          );

          return {
            ...old,
            products: old.products.map((product) => {
              const updatedProduct = updatedProductsMap.get(product.id);
              return updatedProduct || product;
            }),
          };
        },
      );
    },
    onError: (_err, _ids, context) => {
      toast.error(i18n.t("toast.skusFailed"));

      // Revert the optimistic update
      if (context?.previousData) {
        queryClient.setQueriesData(
          { queryKey: ["products"] },
          context.previousData,
        );
      }
    },
  });
};

export const useUpdateCostPrice = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();
  return useMutation({
    mutationFn: async ({
      cost,
      variantId,
    }: {
      cost: number;
      variantId: string;
    }) => {
      const res = await fetch(`${VITE_API_URL}${product_variant_cost_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          cost,
          variant_id: variantId,
        }),
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      return await res.json();
    },
    onSuccess: () => {
      toast.success(i18n.t("toast.costUpdated"));
    },
  });
};

export const useDeleteProducts = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();
  return useMutation({
    mutationFn: async ({ ids, all, filters }: BulkProductParams) => {
      const res = await fetch(`${VITE_API_URL}${products_route}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          ids,
          all,
          filters,
        }),
      });

      // Check if token is invalid and handle logout
      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const token = res.headers.get("access-token");
      if (token) setToken(token);

      // DELETE returns 204 No Content on success
      if (res.status === 204) {
        return { success: true };
      }

      return await res.json();
    },
    onMutate: async ({ ids, all }: BulkProductParams) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["products"]);

      // Optimistically remove deleted products from cache
      queryClient.setQueriesData(
        { queryKey: ["products"] },
        (old: ProductsResponse | undefined) => {
          if (!old || !old.products) return old;

          const remainingProducts = old.products.filter(
            (product) => !all && !ids.includes(product.id),
          );

          return {
            ...old,
            products: remainingProducts,
            pagination: {
              ...old.pagination,
              totalCount: all ? 0 : old.pagination.totalCount - ids.length,
            },
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_data, { ids, count }) => {
      toast.success(
        i18n.t("toast.deletedProducts", { count: count ?? ids.length }),
      );
    },
    onError: (err, _ids, context) => {
      console.log(err);
      toast.error(i18n.t("toast.deleteProductsFailed"));

      // Revert the optimistic update
      if (context?.previousData) {
        queryClient.setQueriesData(
          { queryKey: ["products"] },
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to sync with server
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};
